import { useState, useEffect, useRef } from 'react';
import axios from '@shared/helpers/axios';
import axiosLib, { CancelTokenSource } from 'axios';

const useFetchAPI = <Type>(url: string, isSkip: boolean = false) => {
    const [data, updateData] = useState<Type>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [refetchCount, updateRefetchCount] = useState(0);
    const cancelTokenSourceRef = useRef<CancelTokenSource | null>(null);
    const urlRef = useRef<string>(url);
    const loadingStateRef = useRef<boolean>(false);
    const isUnmountedRef = useRef<boolean>(false);

    // Sync loading state with ref for cleanup
    useEffect(() => {
        loadingStateRef.current = loading;
    }, [loading]);

    // Global cleanup on unmount - ensure loading is always reset
    useEffect(() => {
        isUnmountedRef.current = false;
        return () => {
            isUnmountedRef.current = true;
            console.log('[useFetch] Component unmounting, forcing loading reset');
            // Force reset loading state on unmount - do it immediately, not in setTimeout
            setLoading(false);
            loadingStateRef.current = false;
            // Cancel any pending requests
            if (cancelTokenSourceRef.current) {
                cancelTokenSourceRef.current.cancel('Component unmounted');
                cancelTokenSourceRef.current = null;
            }
        };
    }, []);

    const refetch = (newUrl?: string): void => {
        if (newUrl) {
            urlRef.current = newUrl;
        }
        updateRefetchCount(prev => prev + 1);
    };

    useEffect(() => {
        let isMounted = true;
        let requestId = Date.now(); // Unique ID for this request

        const previousUrl = urlRef.current;
        const urlChanged = previousUrl !== url;

        console.log('[useFetch] URL changed:', {
            oldUrl: previousUrl,
            newUrl: url,
            urlChanged,
            isSkip,
            requestId,
        });

        // ALWAYS cancel previous request when URL changes (aggressive cleanup)
        if (cancelTokenSourceRef.current) {
            console.log('[useFetch] Cancelling previous request (aggressive cleanup)');
            cancelTokenSourceRef.current.cancel('Request cancelled due to URL change');
            cancelTokenSourceRef.current = null;
        }

        // ALWAYS reset loading state when URL changes (before starting new request)
        console.log('[useFetch] Resetting loading state (aggressive reset)');
        setLoading(false);
        setError(false);

        // Update ref when url prop changes
        urlRef.current = url;

        const fetchData = async () => {
            const currentUrl = urlRef.current;
            const currentRequestId = requestId;

            // Check if we should skip this fetch
            if (!isMounted || isSkip) {
                if (isMounted) {
                    setLoading(false);
                }
                return;
            }

            // Double check: Cancel any existing request (defensive programming)
            if (cancelTokenSourceRef.current) {
                console.log('[useFetch] Cancelling existing request in fetchData (defensive)');
                cancelTokenSourceRef.current.cancel('Request cancelled before new fetch');
                cancelTokenSourceRef.current = null;
            }

            // Create new CancelToken for this request
            const cancelTokenSource = axiosLib.CancelToken.source();
            cancelTokenSourceRef.current = cancelTokenSource;

            // Set loading state after creating cancel token
            if (isMounted) {
                console.log('[useFetch] Setting loading to true for URL:', currentUrl);
                setLoading(true);
                setError(false);
            }

            if (currentUrl) {
                try {
                    console.log('[useFetch] Starting fetch for URL:', currentUrl);
                    const { data } = await axios.get(currentUrl, {
                        cancelToken: cancelTokenSource.token,
                    });

                    // Check if request was cancelled, component unmounted, URL changed, or new request started
                    // Use a snapshot of isMounted and urlRef at the time of check
                    const stillMounted = isMounted;
                    const currentUrlAtCheck = urlRef.current;
                    const stillValid =
                        !cancelTokenSource.token.reason &&
                        stillMounted &&
                        currentUrlAtCheck === currentUrl &&
                        requestId === currentRequestId;

                    if (!stillValid) {
                        console.log('[useFetch] Request no longer valid:', {
                            cancelled: !!cancelTokenSource.token.reason,
                            unmounted: !stillMounted,
                            urlChanged: currentUrlAtCheck !== currentUrl,
                            requestIdChanged: requestId !== currentRequestId,
                            currentUrl,
                            currentRequestUrl: currentUrlAtCheck,
                            requestId,
                            currentRequestId,
                        });
                        // Reset loading if this request is no longer valid
                        if (stillMounted) {
                            console.log(
                                '[useFetch] Resetting loading because request is no longer valid'
                            );
                            setLoading(false);
                        }
                        return;
                    }

                    console.log('[useFetch] Fetch successful, updating data', {
                        dataLength: data?.data?.length || 0,
                        currentUrl,
                        requestId,
                        isUnmounted: isUnmountedRef.current,
                    });

                    // Check if component is unmounted before updating state
                    if (isUnmountedRef.current) {
                        console.log('[useFetch] Component unmounted, skipping state update');
                        return;
                    }

                    // Update data and loading state with error handling
                    try {
                        console.log('[useFetch] Updating data and setting loading to false');
                        updateData(data);
                        setLoading(false);
                        loadingStateRef.current = false;
                    } catch (e) {
                        console.error('[useFetch] Error updating state:', e);
                        // Force reset loading even if update fails
                        try {
                            setLoading(false);
                            loadingStateRef.current = false;
                        } catch (e2) {
                            console.error('[useFetch] Error resetting loading:', e2);
                        }
                    }
                } catch (err: any) {
                    console.log('[useFetch] Fetch error:', {
                        error: err,
                        isCancel: axiosLib.isCancel(err),
                        tokenReason: cancelTokenSource.token.reason,
                        isMounted,
                        urlChanged: urlRef.current !== currentUrl,
                        requestIdChanged: requestId !== currentRequestId,
                        currentUrl,
                        currentRequestUrl: urlRef.current,
                    });

                    // Ignore errors from cancelled requests
                    if (axiosLib.isCancel(err) || cancelTokenSource.token.reason) {
                        console.log('[useFetch] Request was cancelled, resetting loading');
                        // Always reset loading state when request is cancelled (don't check isMounted)
                        try {
                            setLoading(false);
                            loadingStateRef.current = false;
                        } catch (e) {
                            console.error('[useFetch] Error resetting loading after cancel:', e);
                        }
                        return;
                    }

                    // Check if component is unmounted before updating error state
                    if (isUnmountedRef.current) {
                        console.log(
                            '[useFetch] Component unmounted during error, skipping state update'
                        );
                        return;
                    }

                    // Only update error state if component is still mounted and URL hasn't changed
                    try {
                        if (
                            isMounted &&
                            urlRef.current === currentUrl &&
                            requestId === currentRequestId
                        ) {
                            console.log('[useFetch] Setting error state');
                            setError(true);
                            setLoading(false);
                            loadingStateRef.current = false;
                        } else if (isMounted) {
                            console.log('[useFetch] URL changed during error, resetting loading');
                            // Reset loading even if URL changed
                            setLoading(false);
                            loadingStateRef.current = false;
                        }
                    } catch (e) {
                        console.error('[useFetch] Error updating error state:', e);
                        // Force reset loading even if update fails
                        try {
                            setLoading(false);
                            loadingStateRef.current = false;
                        } catch (e2) {
                            console.error('[useFetch] Error resetting loading:', e2);
                        }
                    }
                }
            } else {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        // Start fetch immediately (removed setTimeout to avoid race condition)
        console.log('[useFetch] Starting fetchData immediately', { url, requestId });
        fetchData();

        return () => {
            console.log('[useFetch] Cleanup function called', {
                url,
                requestId,
                hasCancelToken: !!cancelTokenSourceRef.current,
                currentUrlRef: urlRef.current,
                loadingState: loadingStateRef.current,
            });

            isMounted = false;

            // ALWAYS cancel request in cleanup (aggressive cleanup to prevent stuck requests)
            if (cancelTokenSourceRef.current) {
                console.log('[useFetch] Cancelling request in cleanup (aggressive cleanup)');
                cancelTokenSourceRef.current.cancel('Component cleanup - cancelling request');
                cancelTokenSourceRef.current = null;
            }

            // ALWAYS reset loading state on cleanup to prevent stuck loading
            // Reset immediately, don't use setTimeout to avoid race conditions
            console.log('[useFetch] Resetting loading in cleanup (aggressive reset)');
            setLoading(false);
            loadingStateRef.current = false;

            // Also check if component is unmounted and force reset
            if (isUnmountedRef.current) {
                console.log('[useFetch] Component already unmounted, forcing loading reset again');
                setLoading(false);
            }
        };
    }, [refetchCount, url, isSkip]);

    return { data, loading, error, refetch };
};

export default useFetchAPI;
